import { Components, Theme } from "@mui/material";
import { palette } from "./palette";
import { typography } from "./typography";

export const components: Components<Omit<Theme, "components">> = {
  MuiButton: {
    styleOverrides: {
      root: {
        ...typography.button,
        borderRadius: "8px",
        textTransform: "none",
        "& .MuiTouchRipple-root": {
          display: "none",
        },
      },
      sizeMedium: {
        padding: "12px 16px",
      },
      sizeLarge: {
        padding: "14px 16px",
      },
    },
    variants: [
      {
        props: { variant: "contained", color: "primary" },
        style: {
          color: palette.common.white,
          backgroundColor: palette.button.primary.main,
          ":hover": {
            color: palette.common.white,
            backgroundColor: palette.button.primary.darker,
          },
          ":focus": {
            color: palette.common.white,
            backgroundColor: palette.button.primary.darker,
            outline: `2px solid ${palette.button.primary.light}`,
          },
          ":disabled": {
            color: palette.common.white,
            backgroundColor: palette.button.primary.main,
            opacity: 0.5,
          },
        },
      },
      {
        props: { variant: "contained", color: "secondary" },
        style: {
          color: palette.text.primary,
          backgroundColor: palette.button.secondary.main,
          ":hover": {
            color: palette.text.primary,
            backgroundColor: palette.button.secondary.dark,
          },
          ":focus": {
            color: palette.text.primary,
            backgroundColor: palette.button.secondary.dark,
            outline: `2px solid ${palette.button.secondary.light}`,
          },
          ":disabled": {
            color: palette.text.primary,
            backgroundColor: palette.button.secondary.main,
            opacity: 0.5,
          },
        },
      },
      {
        props: { variant: "outlined", color: "primary" },
        style: {
          color: palette.button.primary.main,
          border: `1px solid ${palette.button.primary.main}`,
          ":hover": {
            color: palette.button.primary.main,
            backgroundColor: palette.button.primary.light,
            border: `1px solid ${palette.button.primary.main}`,
          },
          ":focus": {
            color: palette.button.primary.main,
            backgroundColor: palette.button.primary.light,
            border: `1px solid ${palette.button.primary.main}`,
            outline: `2px solid ${palette.button.primary.lighter}`,
          },
          ":disabled": {
            color: palette.button.primary.main,
            border: `1px solid ${palette.button.primary.main}`,
            opacity: 0.5,
          },
        },
      },
      {
        props: { variant: "outlined", color: "secondary" },
        style: {
          color: palette.common.black,
          border: `1px solid ${palette.button.secondary.main}`,
          ":hover": {
            color: palette.common.black,
            backgroundColor: palette.button.secondary.light,
            border: `1px solid ${palette.button.secondary.main}`,
          },
          ":focus": {
            color: palette.common.black,
            backgroundColor: palette.button.secondary.light,
            border: `1px solid ${palette.button.secondary.main}`,
            outline: `2px solid ${palette.button.secondary.lighter}`,
          },
          ":disabled": {
            color: palette.common.black,
            border: `1px solid ${palette.button.secondary.main}`,
            opacity: 0.5,
          },
        },
      },
      {
        props: { variant: "text", color: "primary" },
        style: {
          color: palette.button.primary.main,
          backgroundColor: "transparent",
          ":hover": {
            color: palette.button.primary.dark,
            backgroundColor: "transparent",
          },
          ":focus": {
            color: palette.button.primary.dark,
            backgroundColor: "transparent",
            outline: "none",
          },
          ":disabled": {
            color: palette.button.primary.main,
            backgroundColor: "transparent",
            opacity: 0.5,
          },
        },
      },
      {
        props: { variant: "text", color: "secondary" },
        style: {
          color: palette.text.primary,
          backgroundColor: "transparent",
          ":hover": {
            color: palette.common.black,
            backgroundColor: "transparent",
          },
          ":focus": {
            color: palette.common.black,
            backgroundColor: "transparent",
            outline: "none",
          },
          ":disabled": {
            color: palette.text.primary,
            backgroundColor: "transparent",
            opacity: 0.5,
          },
        },
      },
      {
        props: { variant: "soft", color: "primary" },
        style: {
          color: palette.button.primary.main,
          backgroundColor: palette.common.white,
          ":hover": {
            color: palette.button.primary.main,
            backgroundColor: palette.common.white,
          },
          ":focus": {
            color: palette.button.primary.main,
            backgroundColor: palette.common.white,
            outline: `1px solid ${palette.button.primary.lighter}`,
          },
          ":disabled": {
            color: palette.button.primary.main,
            backgroundColor: palette.common.white,
            opacity: 0.5,
          },
        },
      },
      {
        props: { variant: "soft", color: "secondary" },
        style: {
          color: palette.text.primary,
          backgroundColor: palette.common.white,
          ":hover": {
            color: palette.common.black,
            backgroundColor: palette.common.white,
          },
          ":focus": {
            color: palette.text.primary,
            backgroundColor: palette.common.white,
            outline: `1px solid ${palette.button.secondary.lighter}`,
          },
          ":disabled": {
            color: palette.common.black,
            backgroundColor: palette.common.white,
            opacity: 0.5,
          },
        },
      },
    ],
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: "6px",
        ":hover": {
          borderColor: "transparent",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
          },
        },
      },
      iconOpen: {
        color: palette.select.darker,
      },
      outlined: {
        border: `1px solid ${palette.select.main}`,
        borderRadius: "6px",
        ":hover": {
          border: `1px solid ${palette.select.lighter}`,
          outline: `1px solid ${palette.select.lighter}`,
        },
      },
      select: {
        padding: "13px 32px 13px 16px",
      },
    },
  },
  MuiContainer: {
    styleOverrides: {
      root: {
        maxWidth: "1568px !important",
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        color: palette.grey[800],
        textDecoration: "none",
        "&:hover": {
          color: palette.primary.main,
        },
        "&:focus": {
          color: palette.primary.main,
        },
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        boxSizing: "border-box",
        maxWidth: "400px",
        borderRadius: "8px",
        padding: "16px",
        border: "1px solid #F2F4F7",
        boxShadow: "0px 4px 6px -2px rgba(16, 24, 40, 0.03)",
        "& .MuiAlert-icon": {
          marginRight: "15px",
          padding: "0px",
        },
        "& .MuiAlert-action": {
          padding: "0px",
          marginTop: "-8px",
          "& .MuiButtonBase-root": {
            width: "40px",
            height: "40px",
          },
        },
        "& .MuiAlert-message": {
          padding: "0px",
          marginRight: "8px",
        },
      },
      standardSuccess: {
        backgroundColor: palette.common.white,
      },
    },
  },
  MuiRating: {
    styleOverrides: {
      sizeSmall: {
        fontSize: "0.625rem",
      },
      sizeMedium: {
        fontSize: "0.875rem",
      },
      sizeLarge: {
        fontSize: "1.25rem",
      },
      iconFilled: {
        color: palette.primary.main,
      },
      iconHover: {
        color: palette.primary.main,
      },
      iconEmpty: {
        color: "#CED2DA",
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        "&::-webkit-scrollbar": {
          width: "6px",
          backgroundColor: "#f5f5f5",
        },
        "&::-webkit-scrollbar-track": {
          background: "#fff",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "lightgray",
          borderRadius: "20px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "gray",
          cursor: "pointer",
        },
      },
    },
  },
};
